import { useEffect } from "react";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

export default function CGU() {
  useEffect(() => {
    document.title = "Conditions Générales d'Utilisation · FlowMind AI";
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PublicHeader />
      <section className="max-w-3xl mx-auto px-6 py-20" data-testid="cgu-page">
        <span className="overline">Conditions générales</span>
        <h1 className="font-display text-5xl font-medium tracking-tighter mt-4 mb-4">CGU & CGV</h1>
        <p className="text-sm text-zinc-500 mb-12">Dernière mise à jour : 10 février 2026</p>

        <article className="prose prose-zinc max-w-none">
          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 1 — Objet</h2>
          <p className="text-zinc-700 leading-relaxed">
            Les présentes Conditions Générales d'Utilisation et de Vente (« CGU/CGV ») régissent l'accès et
            l'utilisation de la plateforme FlowMind AI, éditée par FlowMind AI SAS. En créant un compte ou en
            souscrivant à un abonnement, l'Utilisateur reconnaît avoir lu et accepté sans réserve l'intégralité
            des présentes conditions.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 2 — Description du service</h2>
          <p className="text-zinc-700 leading-relaxed">
            FlowMind AI est une plateforme SaaS d'automatisation no-code pilotée par intelligence artificielle.
            Elle permet de créer, exécuter et surveiller des workflows reliant différentes applications professionnelles
            (CRM, comptabilité, e-commerce, messagerie, etc.).
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 3 — Inscription et compte</h2>
          <p className="text-zinc-700 leading-relaxed">
            L'Utilisateur doit fournir des informations exactes lors de son inscription. Il est seul responsable
            de la confidentialité de ses identifiants. Un compte est strictement personnel ; toute cession ou
            partage est interdit sans autorisation écrite préalable.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 4 — Essai gratuit</h2>
          <p className="text-zinc-700 leading-relaxed">
            FlowMind AI propose un essai gratuit de 14 jours, sans engagement et sans renseignement de carte bancaire.
            À l'issue de cette période, l'Utilisateur peut choisir un plan payant ou voir son compte automatiquement
            basculé en lecture seule.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 5 — Plans et tarifs</h2>
          <ul className="list-disc pl-6 text-zinc-700 space-y-2 mt-3">
            <li><strong>Starter</strong> — 49 €/mois HT : 5 workflows actifs, 1 000 exécutions/mois.</li>
            <li><strong>Pro</strong> — 149 €/mois HT : 25 workflows, 10 000 exécutions, WhatsApp Business inclus.</li>
            <li><strong>Business</strong> — 399 €/mois HT : workflows illimités, 100 000 exécutions, support prioritaire, SSO.</li>
          </ul>
          <p className="text-zinc-700 leading-relaxed mt-4">
            Les tarifs sont exprimés en euros et hors taxes. La TVA en vigueur est ajoutée lors de la facturation
            (20 % pour les clients français B2C ; auto-liquidation pour les clients B2B intracommunautaires).
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 6 — Paiement et facturation</h2>
          <p className="text-zinc-700 leading-relaxed">
            Les paiements sont prélevés mensuellement via Stripe. Une facture conforme à la réglementation française
            est émise et accessible depuis l'espace client. En cas d'échec de paiement, le compte est suspendu après
            7 jours et résilié après 30 jours.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 7 — Résiliation</h2>
          <p className="text-zinc-700 leading-relaxed">
            L'Utilisateur peut résilier son abonnement à tout moment depuis son espace client. La résiliation prend
            effet à la fin de la période de facturation en cours. Aucun remboursement au prorata n'est effectué.
            FlowMind AI se réserve le droit de résilier un compte en cas de non-respect des CGU.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 8 — Droit de rétractation</h2>
          <p className="text-zinc-700 leading-relaxed">
            Conformément à l'article L221-28 du Code de la consommation, l'Utilisateur consommateur (B2C) dispose
            d'un délai de 14 jours pour exercer son droit de rétractation à compter de la souscription, sauf s'il
            a expressément renoncé à ce droit en commençant l'utilisation du service. Pour les clients professionnels
            (B2B), ce droit ne s'applique pas.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 9 — Propriété du contenu</h2>
          <p className="text-zinc-700 leading-relaxed">
            Toute automatisation, donnée, workflow ou contenu créé par l'Utilisateur reste sa propriété exclusive.
            FlowMind AI n'exploite jamais ces contenus à des fins publicitaires, de revente ou d'entraînement de
            modèles IA tiers.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 10 — Disponibilité du service</h2>
          <p className="text-zinc-700 leading-relaxed">
            FlowMind AI s'engage sur un taux de disponibilité (SLA) de 99,5 % en moyenne mensuelle, hors maintenances
            planifiées annoncées 48 h à l'avance. En cas de manquement, un crédit pro rata peut être appliqué sur
            demande.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 11 — Responsabilité</h2>
          <p className="text-zinc-700 leading-relaxed">
            La responsabilité de FlowMind AI est limitée aux dommages directs et plafonnée au montant payé par
            l'Utilisateur au cours des 12 derniers mois. FlowMind AI ne saurait être tenu responsable des
            dommages indirects (perte de chiffre d'affaires, perte de données après faute de l'Utilisateur, etc.).
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 12 — Modification des CGU</h2>
          <p className="text-zinc-700 leading-relaxed">
            FlowMind AI se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs sont
            informés par email au moins 30 jours avant l'entrée en vigueur des modifications.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">Article 13 — Droit applicable et juridiction</h2>
          <p className="text-zinc-700 leading-relaxed">
            Les présentes CGU sont soumises au droit français. Tout litige relèvera de la compétence exclusive des
            tribunaux de Paris, sous réserve des dispositions impératives applicables aux consommateurs.
          </p>
        </article>
      </section>
      <PublicFooter />
    </div>
  );
}
